import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth.service';
import {
  superadminService,
  Entreprise,
  Licence,
  EntreprisesStats,
  PlanEntreprise,
  CreateEntrepriseDto,
  UtilisateurEntreprise,
  UtilisationEntreprise,
  AuditLog,
  ActiviteEntreprise,
  StatutPaiement,
  Sauvegarde,
} from '../services/superadmin.service';
import './Superadmin.css';

const PLANS: { val: PlanEntreprise; label: string; limites: string }[] = [
  { val: 'basique', label: 'Basique', limites: '1 boutique · 3 utilisateurs · 500 produits' },
  { val: 'pro', label: 'Pro', limites: '3 boutiques · 10 utilisateurs · 5 000 produits' },
  { val: 'premium', label: 'Premium', limites: 'Illimité (999 / 999 / 999 999)' },
];

// Tarif mensuel indicatif par plan (FCFA), pré-rempli à l'émission — modifiable.
const TARIF_MENSUEL: Record<PlanEntreprise, number> = {
  basique: 5000,
  pro: 15000,
  premium: 40000,
};

const entrepriseVide: CreateEntrepriseDto = {
  nom: '', email: '', telephone: '', adresse: '', ifu: '', rccm: '',
  adminEmail: '', adminPassword: '', adminNom: '', adminPrenom: '',
};

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDateHeure = (d?: string) =>
  d
    ? new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const fmtMontant = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} F`;

// Nombre de jours écoulés depuis une date (null si jamais).
const joursDepuis = (d: string | null): number | null =>
  d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null;

// Libellé relatif court : « aujourd'hui », « il y a 3 j », « jamais ».
const depuisLabel = (d: string | null): string => {
  const j = joursDepuis(d);
  if (j === null) return 'jamais';
  if (j <= 0) return "aujourd'hui";
  if (j === 1) return 'hier';
  return `il y a ${j} j`;
};

// Jours restants avant une échéance (négatif si dépassée).
const joursAvant = (d: string): number =>
  Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

// Badge d'échéance : rouge ≤ 7 j, orange ≤ 30 j, sinon neutre.
const badgeEcheance = (dateExpiration: string) => {
  const j = joursAvant(dateExpiration);
  if (j < 0) return <span className="sa-badge sa-red">Expirée</span>;
  if (j <= 7) return <span className="sa-badge sa-red">{j} j restants</span>;
  if (j <= 30) return <span className="sa-badge sa-amber">{j} j restants</span>;
  return <span className="sa-badge sa-grey">{j} j</span>;
};

const planLabel = (p: PlanEntreprise) => PLANS.find((x) => x.val === p)?.label || p;

const badgePaiement = (s: string) => {
  if (s === 'paye') return <span className="sa-badge sa-green">Payé</span>;
  if (s === 'partiel') return <span className="sa-badge sa-amber">Partiel</span>;
  return <span className="sa-badge sa-red">Impayé</span>;
};

// Libellés lisibles des actions journalisées, et couleur associée.
const ACTIONS_AUDIT: Record<string, { label: string; ton: string }> = {
  'entreprise.creer': { label: 'Création entreprise', ton: 'sa-green' },
  'entreprise.modifier': { label: 'Modification entreprise', ton: 'sa-grey' },
  'entreprise.suspendre': { label: 'Suspension entreprise', ton: 'sa-red' },
  'entreprise.reactiver': { label: 'Réactivation entreprise', ton: 'sa-green' },
  'entreprise.supprimer': { label: 'Suppression entreprise', ton: 'sa-red' },
  'utilisateur.creer-admin': { label: 'Création admin', ton: 'sa-green' },
  'utilisateur.reset-password': { label: 'Réinit. mot de passe', ton: 'sa-amber' },
  'licence.emettre': { label: 'Émission licence', ton: 'sa-green' },
  'licence.revoquer': { label: 'Révocation licence', ton: 'sa-red' },
  'licence.renouveler': { label: 'Renouvellement licence', ton: 'sa-green' },
  'licence.paiement': { label: 'Paiement licence', ton: 'sa-grey' },
};
const badgeAction = (a: string) => {
  const info = ACTIONS_AUDIT[a] || { label: a, ton: 'sa-grey' };
  return <span className={`sa-badge ${info.ton}`}>{info.label}</span>;
};

const badgeEntreprise = (statut: string) => {
  if (statut === 'active') return <span className="sa-badge sa-green">● Active</span>;
  if (statut === 'suspendue') return <span className="sa-badge sa-red">● Suspendue</span>;
  return <span className="sa-badge sa-amber">● Expirée</span>;
};

const badgeLicence = (statut: string) => {
  const map: Record<string, string> = {
    active: 'sa-green', expiree: 'sa-amber', suspendue: 'sa-red', revoquee: 'sa-grey',
  };
  const label: Record<string, string> = {
    active: 'Active', expiree: 'Expirée', suspendue: 'Suspendue', revoquee: 'Révoquée',
  };
  return <span className={`sa-badge ${map[statut] || 'sa-grey'}`}>{label[statut] || statut}</span>;
};

export default function Superadmin() {
  const user = authService.getUser();
  const [page, setPage] = useState<'dashboard' | 'entreprises' | 'licences' | 'audit' | 'sauvegardes'>('dashboard');
  const [stats, setStats] = useState<EntreprisesStats | null>(null);
  const [entreprises, setEntreprises] = useState<Entreprise[]>([]);
  const [licences, setLicences] = useState<Licence[]>([]);
  const [audits, setAudits] = useState<AuditLog[]>([]);
  const [auditCharge, setAuditCharge] = useState(false);
  const [sauvegardes, setSauvegardes] = useState<Sauvegarde[]>([]);
  const [dossierSvg, setDossierSvg] = useState('');
  const [svgCharge, setSvgCharge] = useState(false);
  const [svgEnCours, setSvgEnCours] = useState(false);
  const [msgSvg, setMsgSvg] = useState('');
  const [activites, setActivites] = useState<ActiviteEntreprise[]>([]);
  const [mailStatut, setMailStatut] = useState<{ actif: boolean; emetteur: string | null; destinataireAlertes: string | null } | null>(null);
  const [mailTestDest, setMailTestDest] = useState('');
  const [mailTestEnCours, setMailTestEnCours] = useState(false);
  const [mailTestResultat, setMailTestResultat] = useState<string>('');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  // Modale entreprise
  const [showEntreprise, setShowEntreprise] = useState(false);
  const [entrepriseEnEdition, setEntrepriseEnEdition] = useState<number | null>(null);
  const [formEntreprise, setFormEntreprise] = useState<CreateEntrepriseDto>(entrepriseVide);
  // Licence émise d'office à la création d'une entreprise.
  const [creationPlan, setCreationPlan] = useState<PlanEntreprise>('basique');
  const [creationDuree, setCreationDuree] = useState(12);

  // Modale détail entreprise
  const [showDetail, setShowDetail] = useState(false);
  const [detailEntreprise, setDetailEntreprise] = useState<Entreprise | null>(null);
  const [detailUsers, setDetailUsers] = useState<UtilisateurEntreprise[]>([]);
  const [detailUsage, setDetailUsage] = useState<UtilisationEntreprise | null>(null);
  const [detailLicences, setDetailLicences] = useState<Licence[]>([]);
  const [detailChargement, setDetailChargement] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [formAdmin, setFormAdmin] = useState({ prenom: '', nom: '', email: '', password: '' });

  // Modale changement de mot de passe (super-admin)
  const [showMdp, setShowMdp] = useState(false);
  const [formMdp, setFormMdp] = useState({ ancien: '', nouveau: '', confirme: '' });

  // Modale de suppression définitive d'une entreprise
  const [showSuppression, setShowSuppression] = useState(false);
  const [confirmationNom, setConfirmationNom] = useState('');
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  // Modale licence
  const [showLicence, setShowLicence] = useState(false);
  const [formLicence, setFormLicence] = useState<{ entrepriseId: number | ''; plan: PlanEntreprise; dureeMois: number; dureeHeures: number; montant: number; statutPaiement: StatutPaiement; notes: string }>(
    { entrepriseId: '', plan: 'basique', dureeMois: 12, dureeHeures: 0, montant: TARIF_MENSUEL.basique * 12, statutPaiement: 'impaye', notes: '' },
  );

  // Modale de renouvellement de licence
  const [showRenouveler, setShowRenouveler] = useState(false);
  const [licenceARenouveler, setLicenceARenouveler] = useState<Licence | null>(null);
  const [renouvellerDuree, setRenouvellerDuree] = useState(12);
  const [renouvellerHeures, setRenouvellerHeures] = useState(0);
  const [renouvellerMontant, setRenouvellerMontant] = useState(0);
  const [renouvellerPaiement, setRenouvellerPaiement] = useState<StatutPaiement>('paye');

  const flash = (msg: string) => {
    setSucces(msg);
    setTimeout(() => setSucces(''), 3500);
  };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [s, ents, lics, acts] = await Promise.all([
        superadminService.statsEntreprises(),
        superadminService.listerEntreprises(),
        superadminService.listerLicences(),
        superadminService.listerActivite().catch(() => []),
      ]);
      setStats(s);
      setEntreprises(ents);
      setLicences(lics);
      setActivites(acts);
      superadminService.statutMail().then(setMailStatut).catch(() => setMailStatut(null));
      // Une action vient d'aboutir : le journal sera rechargé au prochain affichage.
      setAuditCharge(false);
      setErreur('');
    } catch (e: any) {
      setErreur('Impossible de charger les données super-administrateur.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const chargerAudit = useCallback(async () => {
    try {
      setAudits(await superadminService.listerAudit(200));
      setAuditCharge(true);
    } catch (e) {
      setErreur("Impossible de charger le journal d'audit.");
    }
  }, []);

  // Chargement paresseux du journal la première fois qu'on ouvre l'onglet.
  useEffect(() => {
    if (page === 'audit' && !auditCharge) chargerAudit();
  }, [page, auditCharge, chargerAudit]);

  const chargerSauvegardes = useCallback(async () => {
    try {
      const [liste, d] = await Promise.all([
        superadminService.listerSauvegardes(),
        superadminService.dossierSauvegardes().catch(() => ({ dossier: '' })),
      ]);
      setSauvegardes(liste);
      setDossierSvg(d.dossier);
      setSvgCharge(true);
    } catch (e) {
      setErreur('Impossible de charger les sauvegardes.');
    }
  }, []);

  useEffect(() => {
    if (page === 'sauvegardes' && !svgCharge) chargerSauvegardes();
  }, [page, svgCharge, chargerSauvegardes]);

  const lancerSauvegarde = async () => {
    setSvgEnCours(true);
    setMsgSvg('');
    try {
      const r = await superadminService.lancerSauvegarde();
      setMsgSvg(r.succes ? `✅ ${r.message} : ${r.fichier}` : `⚠️ ${r.message}`);
      if (r.succes) chargerSauvegardes();
    } catch {
      setMsgSvg('❌ Échec du lancement de la sauvegarde.');
    } finally {
      setSvgEnCours(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Se déconnecter de la console NexaCom ?')) {
      authService.logout();
      window.location.href = '/';
    }
  };

  const nomEntreprise = (id: number) => entreprises.find((e) => e.id === id)?.nom || `#${id}`;

  // --- Entreprise : création / édition ---
  const ouvrirCreationEntreprise = () => {
    setEntrepriseEnEdition(null);
    setFormEntreprise(entrepriseVide);
    setCreationPlan('basique');
    setCreationDuree(12);
    setErreur('');
    setShowEntreprise(true);
  };

  const ouvrirEditionEntreprise = (e: Entreprise) => {
    setEntrepriseEnEdition(e.id);
    setFormEntreprise({
      nom: e.nom, email: e.email || '', telephone: e.telephone || '',
      adresse: e.adresse || '', ifu: e.ifu || '', rccm: e.rccm || '',
    });
    setErreur('');
    setShowEntreprise(true);
  };

  const sauvegarderEntreprise = async () => {
    if (!formEntreprise.nom.trim()) { setErreur('Le nom de l\'entreprise est obligatoire'); return; }
    if (!entrepriseEnEdition) {
      if (!formEntreprise.adminEmail?.trim()) { setErreur('L\'email de l\'administrateur est obligatoire'); return; }
      if (!formEntreprise.adminPassword || formEntreprise.adminPassword.length < 6) {
        setErreur('Le mot de passe de l\'administrateur doit faire au moins 6 caractères');
        return;
      }
    }
    try {
      if (entrepriseEnEdition) {
        // En édition : ne pas renvoyer les champs admin (création uniquement).
        const { adminEmail, adminPassword, adminNom, adminPrenom, ...maj } = formEntreprise;
        await superadminService.modifierEntreprise(entrepriseEnEdition, maj);
        flash('Entreprise modifiée');
      } else {
        const entreprise = await superadminService.creerEntreprise(formEntreprise);
        // Émet une licence d'office pour activer immédiatement l'entreprise.
        await superadminService.emettreLicence({
          entrepriseId: entreprise.id,
          plan: creationPlan,
          dureeMois: creationDuree,
        });
        flash('Entreprise créée, compte administrateur et licence activés');
      }
      setShowEntreprise(false);
      charger();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const basculerStatut = async (e: Entreprise) => {
    const nouveau = e.statut === 'suspendue' ? 'active' : 'suspendue';
    const verbe = nouveau === 'suspendue' ? 'Suspendre' : 'Réactiver';
    if (!window.confirm(`${verbe} l'accès de « ${e.nom} » ?`)) return;
    try {
      await superadminService.modifierEntreprise(e.id, { statut: nouveau });
      flash(`Entreprise ${nouveau === 'suspendue' ? 'suspendue' : 'réactivée'}`);
      charger();
    } catch {
      setErreur('Erreur lors du changement de statut');
    }
  };

  // --- Détail entreprise : usage, licences, utilisateurs ---
  const ouvrirDetail = async (e: Entreprise) => {
    setDetailEntreprise(e);
    setShowDetail(true);
    setShowAdminForm(false);
    setFormAdmin({ prenom: '', nom: '', email: '', password: '' });
    setErreur('');
    setDetailChargement(true);
    try {
      const [users, usage, lics] = await Promise.all([
        superadminService.utilisateursEntreprise(e.id),
        superadminService.utilisationEntreprise(e.id),
        superadminService.licencesEntreprise(e.id),
      ]);
      setDetailUsers(users);
      setDetailUsage(usage);
      setDetailLicences(lics);
    } catch {
      setErreur('Impossible de charger le détail de l\'entreprise');
    } finally {
      setDetailChargement(false);
    }
  };

  const rechargerDetail = async () => {
    if (!detailEntreprise) return;
    const [users, usage] = await Promise.all([
      superadminService.utilisateursEntreprise(detailEntreprise.id),
      superadminService.utilisationEntreprise(detailEntreprise.id),
    ]);
    setDetailUsers(users);
    setDetailUsage(usage);
  };

  const ajouterAdmin = async () => {
    if (!detailEntreprise) return;
    if (!formAdmin.email.trim()) { setErreur('L\'email est obligatoire'); return; }
    if (formAdmin.password.length < 6) { setErreur('Le mot de passe doit faire au moins 6 caractères'); return; }
    try {
      await superadminService.creerAdmin(detailEntreprise.id, {
        email: formAdmin.email.trim(),
        password: formAdmin.password,
        nom: formAdmin.nom.trim() || undefined,
        prenom: formAdmin.prenom.trim() || undefined,
      });
      flash('Administrateur ajouté');
      setShowAdminForm(false);
      setFormAdmin({ prenom: '', nom: '', email: '', password: '' });
      setErreur('');
      await rechargerDetail();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de la création de l\'administrateur');
    }
  };

  const reinitMdp = async (u: UtilisateurEntreprise) => {
    if (!detailEntreprise) return;
    const nouveau = window.prompt(`Nouveau mot de passe pour ${u.prenom} ${u.nom} (${u.email}) :`);
    if (nouveau == null) return;
    if (nouveau.length < 6) { setErreur('Le mot de passe doit faire au moins 6 caractères'); return; }
    try {
      await superadminService.resetMotDePasse(detailEntreprise.id, u.id, nouveau);
      flash(`Mot de passe réinitialisé pour ${u.email}`);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de la réinitialisation');
    }
  };

  // --- Suppression définitive d'une entreprise (toutes ses données) ---
  const supprimerEntreprise = async () => {
    if (!detailEntreprise) return;
    if (confirmationNom !== detailEntreprise.nom) return;
    setSuppressionEnCours(true);
    try {
      await superadminService.supprimerEntreprise(detailEntreprise.id);
      flash(`Entreprise « ${detailEntreprise.nom} » supprimée définitivement`);
      setShowSuppression(false);
      setShowDetail(false);
      setDetailEntreprise(null);
      setConfirmationNom('');
      setErreur('');
      charger();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setSuppressionEnCours(false);
    }
  };

  // --- Mot de passe du super-admin ---
  const changerMonMdp = async () => {
    if (formMdp.nouveau.length < 6) { setErreur('Le nouveau mot de passe doit faire au moins 6 caractères'); return; }
    if (formMdp.nouveau !== formMdp.confirme) { setErreur('La confirmation ne correspond pas'); return; }
    try {
      await authService.changerMotDePasse(formMdp.ancien, formMdp.nouveau);
      flash('Votre mot de passe a été modifié');
      setShowMdp(false);
      setFormMdp({ ancien: '', nouveau: '', confirme: '' });
      setErreur('');
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors du changement de mot de passe');
    }
  };

  // --- Licence : émission / révocation ---
  const ouvrirEmissionLicence = (entrepriseId?: number) => {
    setFormLicence({
      entrepriseId: entrepriseId ?? '', plan: 'basique', dureeMois: 12, dureeHeures: 0,
      montant: TARIF_MENSUEL.basique * 12, statutPaiement: 'impaye', notes: '',
    });
    setErreur('');
    setShowLicence(true);
  };

  // Recalcule le montant proposé quand le plan change (0 en mode test).
  const majPlanDuree = (plan: PlanEntreprise, dureeMois: number) => {
    setFormLicence((f) => ({
      ...f, plan, dureeMois,
      montant: f.dureeHeures > 0 ? 0 : TARIF_MENSUEL[plan] * dureeMois,
    }));
  };

  // Gère le sélecteur de durée : « test » = 72 h gratuit, sinon durée en mois.
  const majDuree = (valeur: string) => {
    if (valeur === 'test') {
      setFormLicence((f) => ({
        ...f, dureeHeures: 72, montant: 0,
        notes: f.notes || 'Licence de test 72 h',
      }));
    } else {
      const d = Number(valeur);
      setFormLicence((f) => ({
        ...f, dureeMois: d, dureeHeures: 0, montant: TARIF_MENSUEL[f.plan] * d,
      }));
    }
  };

  const emettreLicence = async () => {
    if (formLicence.entrepriseId === '') { setErreur('Sélectionnez une entreprise'); return; }
    try {
      const estTest = formLicence.dureeHeures > 0;
      await superadminService.emettreLicence({
        entrepriseId: Number(formLicence.entrepriseId),
        plan: formLicence.plan,
        ...(estTest
          ? { dureeHeures: formLicence.dureeHeures }
          : { dureeMois: formLicence.dureeMois }),
        montant: formLicence.montant,
        statutPaiement: formLicence.statutPaiement,
        notes: formLicence.notes || undefined,
      });
      flash(estTest ? 'Licence de test émise (72 h)' : 'Licence émise et plan appliqué');
      setShowLicence(false);
      charger();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de l\'émission');
    }
  };

  const marquerPaiement = async (l: Licence, statutPaiement: StatutPaiement) => {
    try {
      await superadminService.marquerPaiement(l.id, statutPaiement);
      flash(statutPaiement === 'paye' ? 'Licence marquée payée' : 'Paiement mis à jour');
      charger();
    } catch {
      setErreur('Erreur lors de la mise à jour du paiement');
    }
  };

  const revoquerLicence = async (l: Licence) => {
    if (!window.confirm(`Révoquer la licence ${l.cle} ?\nL'entreprise sera suspendue.`)) return;
    try {
      await superadminService.revoquerLicence(l.id);
      flash('Licence révoquée');
      charger();
    } catch {
      setErreur('Erreur lors de la révocation');
    }
  };

  const ouvrirRenouvellement = (l: Licence) => {
    setLicenceARenouveler(l);
    setRenouvellerDuree(12);
    setRenouvellerHeures(0);
    setRenouvellerMontant(TARIF_MENSUEL[l.plan] * 12);
    setRenouvellerPaiement('paye');
    setErreur('');
    setShowRenouveler(true);
  };

  // Sélecteur de prolongation : « test » = +72 h gratuit, sinon durée en mois.
  const majRenouvellerDuree = (valeur: string) => {
    if (!licenceARenouveler) return;
    if (valeur === 'test') {
      setRenouvellerHeures(72);
      setRenouvellerMontant(0);
    } else {
      const d = Number(valeur);
      setRenouvellerHeures(0);
      setRenouvellerDuree(d);
      setRenouvellerMontant(TARIF_MENSUEL[licenceARenouveler.plan] * d);
    }
  };

  const confirmerRenouvellement = async () => {
    if (!licenceARenouveler) return;
    const estTest = renouvellerHeures > 0;
    try {
      await superadminService.renouvelerLicence(
        licenceARenouveler.id,
        renouvellerDuree,
        renouvellerMontant,
        renouvellerPaiement,
        estTest ? renouvellerHeures : undefined,
      );
      setShowRenouveler(false);
      flash(estTest ? 'Licence de test prolongée (+72 h)' : `Licence renouvelée (+${renouvellerDuree} mois)`);
      charger();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors du renouvellement');
    }
  };

  const testerMail = async () => {
    setMailTestEnCours(true);
    setMailTestResultat('');
    try {
      const r = await superadminService.testerMail(mailTestDest || undefined);
      setMailTestResultat(
        r.ok
          ? `✅ E-mail de test envoyé à ${r.destinataire}.`
          : `❌ Échec : ${r.erreur || 'erreur inconnue'}`,
      );
    } catch (e: any) {
      setMailTestResultat(`❌ Échec : ${e.response?.data?.erreur || e.response?.data?.message || 'erreur réseau'}`);
    } finally {
      setMailTestEnCours(false);
    }
  };

  const navItem = (p: typeof page, icon: string, label: string) => (
    <div className={`sa-nav-item ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>
      <span>{icon}</span> {label}
    </div>
  );

  return (
    <div className="sa-layout">
      {/* SIDEBAR */}
      <aside className="sa-sidebar">
        <div className="sa-logo">
          <h2>NexaCom</h2>
          <span>Console éditeur</span>
        </div>
        <nav className="sa-nav">
          <div className="sa-nav-section">Supervision</div>
          {navItem('dashboard', '📊', 'Vue d\'ensemble')}
          {navItem('entreprises', '🏢', 'Entreprises')}
          {navItem('licences', '🔑', 'Licences')}
          {navItem('audit', '📜', 'Journal d\'audit')}
          {navItem('sauvegardes', '💾', 'Sauvegardes')}
        </nav>
        <div className="sa-footer">
          <div className="sa-user">
            <div className="sa-avatar">{user?.prenom?.[0]}{user?.nom?.[0]}</div>
            <div>
              <div className="sa-user-name">{user?.prenom} {user?.nom}</div>
              <div className="sa-user-role">Super-administrateur</div>
            </div>
          </div>
          <button className="sa-btn-logout" onClick={() => { setFormMdp({ ancien: '', nouveau: '', confirme: '' }); setErreur(''); setShowMdp(true); }}>Changer mon mot de passe</button>
          <button className="sa-btn-logout" onClick={handleLogout}>Déconnexion</button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="sa-main">
        {succes && <div className="sa-alert sa-alert-ok">✅ {succes}</div>}
        {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}

        {chargement ? (
          <div className="sa-loading">Chargement de la console…</div>
        ) : (
          <>
            {/* ===== VUE D'ENSEMBLE ===== */}
            {page === 'dashboard' && (
              <div className="sa-content">
                <header className="sa-header">
                  <div>
                    <h1>Vue d'ensemble</h1>
                    <p>Supervision des entreprises clientes NexaCom</p>
                  </div>
                </header>

                <div className="sa-kpi-grid">
                  <div className="sa-kpi sa-kpi-blue">
                    <div className="sa-kpi-val">{stats?.total ?? 0}</div>
                    <div className="sa-kpi-lbl">Entreprises au total</div>
                  </div>
                  <div className="sa-kpi sa-kpi-green">
                    <div className="sa-kpi-val">{stats?.actives ?? 0}</div>
                    <div className="sa-kpi-lbl">Actives</div>
                  </div>
                  <div className="sa-kpi sa-kpi-red">
                    <div className="sa-kpi-val">{stats?.suspendues ?? 0}</div>
                    <div className="sa-kpi-lbl">Suspendues</div>
                  </div>
                  <div className="sa-kpi sa-kpi-amber">
                    <div className="sa-kpi-val">{stats?.expirees ?? 0}</div>
                    <div className="sa-kpi-lbl">Expirées</div>
                  </div>
                </div>

                {(() => {
                  const bientot = licences.filter(
                    (l) => l.statut === 'active' && joursAvant(l.dateExpiration) <= 15,
                  );
                  if (bientot.length === 0) return null;
                  return (
                    <div className="sa-alert sa-alert-warn">
                      ⏰ {bientot.length} licence{bientot.length > 1 ? 's' : ''} arrive
                      {bientot.length > 1 ? 'nt' : ''} à échéance sous 15 jours — pensez à renouveler.
                    </div>
                  );
                })()}

                <div className="sa-card">
                  <div className="sa-card-head">
                    <h2>Licences — prochaines échéances</h2>
                  </div>
                  <table className="sa-table">
                    <thead>
                      <tr><th>Entreprise</th><th>Plan</th><th>Expiration</th><th>Échéance</th><th></th></tr>
                    </thead>
                    <tbody>
                      {licences
                        .filter((l) => l.statut === 'active')
                        .sort((a, b) => +new Date(a.dateExpiration) - +new Date(b.dateExpiration))
                        .slice(0, 8)
                        .map((l) => (
                          <tr key={l.id}>
                            <td>{nomEntreprise(l.entrepriseId)}</td>
                            <td>{planLabel(l.plan)}</td>
                            <td>{fmtDate(l.dateExpiration)}</td>
                            <td>{badgeEcheance(l.dateExpiration)}</td>
                            <td className="sa-actions">
                              <button className="sa-btn-mini sa-mini-green" onClick={() => ouvrirRenouvellement(l)}>Renouveler</button>
                            </td>
                          </tr>
                        ))}
                      {licences.filter((l) => l.statut === 'active').length === 0 && (
                        <tr><td colSpan={5} className="sa-empty">Aucune licence active.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="sa-card">
                  <div className="sa-card-head">
                    <h2>Activité des entreprises</h2>
                  </div>
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Entreprise</th><th>Dernière connexion</th><th>Dernière vente</th>
                        <th>Ventes (30 j)</th><th>CA (30 j)</th><th>CA total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...activites]
                        .sort((a, b) => (b.ca30j - a.ca30j))
                        .map((a) => {
                          // Client « dormant » : aucune vente et aucune connexion depuis 30 j.
                          const dormant =
                            (joursDepuis(a.derniereVente) ?? 999) > 30 &&
                            (joursDepuis(a.derniereConnexion) ?? 999) > 30;
                          return (
                            <tr key={a.entrepriseId}>
                              <td>
                                <div className="sa-ent-nom">{a.nom}</div>
                                {dormant && <span className="sa-badge sa-grey">Dormant</span>}
                              </td>
                              <td className="sa-limites">{depuisLabel(a.derniereConnexion)}</td>
                              <td className="sa-limites">{depuisLabel(a.derniereVente)}</td>
                              <td>{a.nbVentes30j}</td>
                              <td>{fmtMontant(a.ca30j)}</td>
                              <td className="sa-limites">{fmtMontant(a.caTotal)}</td>
                            </tr>
                          );
                        })}
                      {activites.length === 0 && (
                        <tr><td colSpan={6} className="sa-empty">Aucune donnée d'activité.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="sa-card">
                  <div className="sa-card-head">
                    <h2>Alertes e-mail</h2>
                  </div>
                  <div style={{ padding: '16px 18px' }}>
                    {mailStatut ? (
                      <>
                        <p style={{ margin: '0 0 12px' }}>
                          Statut SMTP :{' '}
                          {mailStatut.actif
                            ? <span className="sa-badge sa-green">Configuré</span>
                            : <span className="sa-badge sa-red">Non configuré</span>}
                          {mailStatut.actif && mailStatut.emetteur && (
                            <span className="sa-ent-sub"> · expéditeur {mailStatut.emetteur}</span>
                          )}
                        </p>
                        {mailStatut.actif ? (
                          <>
                            <div className="sa-form-row" style={{ alignItems: 'end' }}>
                              <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                <label>Envoyer un e-mail de test à</label>
                                <input
                                  value={mailTestDest}
                                  onChange={(e) => setMailTestDest(e.target.value)}
                                  placeholder={mailStatut.destinataireAlertes || 'adresse@exemple.com'} />
                              </div>
                              <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                <button className="sa-btn-primary" onClick={testerMail} disabled={mailTestEnCours}>
                                  {mailTestEnCours ? 'Envoi…' : 'Tester'}
                                </button>
                              </div>
                            </div>
                            {mailTestResultat && <p className="sa-hint" style={{ marginTop: 10 }}>{mailTestResultat}</p>}
                          </>
                        ) : (
                          <p className="sa-hint">
                            Renseignez les variables SMTP dans le fichier <code>.env</code> du serveur
                            (SMTP_HOST, SMTP_USER, SMTP_PASS…) puis redémarrez le backend.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="sa-hint">Statut e-mail indisponible.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ===== ENTREPRISES ===== */}
            {page === 'entreprises' && (
              <div className="sa-content">
                <header className="sa-header">
                  <div>
                    <h1>Entreprises</h1>
                    <p>Gérer les tenants clients et leur abonnement</p>
                  </div>
                  <button className="sa-btn-primary" onClick={ouvrirCreationEntreprise}>+ Nouvelle entreprise</button>
                </header>

                <div className="sa-card">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Entreprise</th><th>Plan</th><th>Limites</th><th>Statut</th><th>Créée le</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entreprises.map((e) => (
                        <tr key={e.id}>
                          <td>
                            <div className="sa-ent-nom">{e.nom}</div>
                            <div className="sa-ent-sub">{e.slug}{e.email ? ` · ${e.email}` : ''}</div>
                          </td>
                          <td>{planLabel(e.plan)}</td>
                          <td className="sa-limites">{e.maxBoutiques} 🏪 · {e.maxUtilisateurs} 👤 · {e.maxProduits} 📦</td>
                          <td>{badgeEntreprise(e.statut)}</td>
                          <td>{fmtDate(e.createdAt)}</td>
                          <td className="sa-actions">
                            <button className="sa-btn-mini" onClick={() => ouvrirDetail(e)}>Détails</button>
                            <button className="sa-btn-mini" onClick={() => ouvrirEditionEntreprise(e)}>Modifier</button>
                            <button className="sa-btn-mini" onClick={() => ouvrirEmissionLicence(e.id)}>Licence</button>
                            <button
                              className={`sa-btn-mini ${e.statut === 'suspendue' ? 'sa-mini-green' : 'sa-mini-red'}`}
                              onClick={() => basculerStatut(e)}
                            >
                              {e.statut === 'suspendue' ? 'Réactiver' : 'Suspendre'}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {entreprises.length === 0 && (
                        <tr><td colSpan={6} className="sa-empty">Aucune entreprise.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ===== LICENCES ===== */}
            {page === 'licences' && (
              <div className="sa-content">
                <header className="sa-header">
                  <div>
                    <h1>Licences</h1>
                    <p>Émettre et révoquer les licences d'activation</p>
                  </div>
                  <button className="sa-btn-primary" onClick={() => ouvrirEmissionLicence()}>+ Émettre une licence</button>
                </header>

                {(() => {
                  // Récap facturation sur les licences non révoquées.
                  const facturables = licences.filter((l) => l.statut !== 'revoquee');
                  const totalFacture = facturables.reduce((s, l) => s + Number(l.montant || 0), 0);
                  const encaisse = facturables
                    .filter((l) => l.statutPaiement === 'paye')
                    .reduce((s, l) => s + Number(l.montant || 0), 0);
                  const impaye = facturables
                    .filter((l) => l.statutPaiement !== 'paye')
                    .reduce((s, l) => s + Number(l.montant || 0), 0);
                  return (
                    <div className="sa-kpi-grid">
                      <div className="sa-kpi sa-kpi-blue">
                        <div className="sa-kpi-val" style={{ fontSize: 22 }}>{fmtMontant(totalFacture)}</div>
                        <div className="sa-kpi-lbl">Total facturé</div>
                      </div>
                      <div className="sa-kpi sa-kpi-green">
                        <div className="sa-kpi-val" style={{ fontSize: 22 }}>{fmtMontant(encaisse)}</div>
                        <div className="sa-kpi-lbl">Encaissé</div>
                      </div>
                      <div className="sa-kpi sa-kpi-red">
                        <div className="sa-kpi-val" style={{ fontSize: 22 }}>{fmtMontant(impaye)}</div>
                        <div className="sa-kpi-lbl">Impayé / en attente</div>
                      </div>
                      <div className="sa-kpi sa-kpi-amber">
                        <div className="sa-kpi-val">{facturables.filter((l) => l.statutPaiement !== 'paye').length}</div>
                        <div className="sa-kpi-lbl">Licences à encaisser</div>
                      </div>
                    </div>
                  );
                })()}

                <div className="sa-card">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Clé</th><th>Entreprise</th><th>Plan</th><th>Montant</th><th>Paiement</th><th>Statut</th><th>Expiration</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {licences.map((l) => (
                        <tr key={l.id}>
                          <td className="sa-cle">{l.cle}</td>
                          <td>{nomEntreprise(l.entrepriseId)}</td>
                          <td>{planLabel(l.plan)}</td>
                          <td className="sa-limites">{fmtMontant(Number(l.montant || 0))}</td>
                          <td>{badgePaiement(l.statutPaiement)}</td>
                          <td>{badgeLicence(l.statut)}</td>
                          <td>{fmtDate(l.dateExpiration)}</td>
                          <td className="sa-actions">
                            {l.statutPaiement !== 'paye' && l.statut !== 'revoquee' && (
                              <button className="sa-btn-mini sa-mini-green" onClick={() => marquerPaiement(l, 'paye')}>Marquer payé</button>
                            )}
                            {(l.statut === 'active' || l.statut === 'expiree') && (
                              <button className="sa-btn-mini" onClick={() => ouvrirRenouvellement(l)}>Renouveler</button>
                            )}
                            {l.statut === 'active' && (
                              <button className="sa-btn-mini sa-mini-red" onClick={() => revoquerLicence(l)}>Révoquer</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {licences.length === 0 && (
                        <tr><td colSpan={8} className="sa-empty">Aucune licence émise.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ===== JOURNAL D'AUDIT ===== */}
            {page === 'audit' && (
              <div className="sa-content">
                <header className="sa-header">
                  <div>
                    <h1>Journal d'audit</h1>
                    <p>Traçabilité des actions sensibles de la console (200 plus récentes)</p>
                  </div>
                  <button className="sa-btn-secondary" onClick={chargerAudit}>↻ Actualiser</button>
                </header>

                <div className="sa-card">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Date &amp; heure</th><th>Action</th><th>Cible</th><th>Détails</th><th>Auteur</th><th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audits.map((a) => (
                        <tr key={a.id}>
                          <td className="sa-limites">{fmtDateHeure(a.createdAt)}</td>
                          <td>{badgeAction(a.action)}</td>
                          <td>
                            {a.cibleLabel || (a.cibleId != null ? `#${a.cibleId}` : '—')}
                            {a.cibleType && <div className="sa-ent-sub">{a.cibleType}</div>}
                          </td>
                          <td className="sa-ent-sub">{a.details || '—'}</td>
                          <td>{a.acteurEmail || '—'}</td>
                          <td className="sa-cle">{a.ip || '—'}</td>
                        </tr>
                      ))}
                      {audits.length === 0 && (
                        <tr><td colSpan={6} className="sa-empty">Aucune action journalisée pour le moment.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ===== SAUVEGARDES ===== */}
            {page === 'sauvegardes' && (
              <div className="sa-content">
                <header className="sa-header">
                  <div>
                    <h1>Sauvegardes</h1>
                    <p>Copies de la base complète (toutes les entreprises) — exploitation NexaCom</p>
                  </div>
                  <button className="sa-btn-primary" onClick={lancerSauvegarde} disabled={svgEnCours}>
                    {svgEnCours ? 'Sauvegarde en cours…' : '💾 Lancer une sauvegarde'}
                  </button>
                </header>

                {msgSvg && <div className="sa-alert sa-alert-ok">{msgSvg}</div>}

                <div className="sa-kpi-grid">
                  <div className="sa-kpi sa-kpi-blue">
                    <div className="sa-kpi-val">{sauvegardes.length}</div>
                    <div className="sa-kpi-lbl">Sauvegardes conservées</div>
                  </div>
                  <div className="sa-kpi sa-kpi-green">
                    <div className="sa-kpi-val sa-limites" style={{ fontSize: 15, fontWeight: 600 }}>
                      {sauvegardes[0] ? fmtDateHeure(sauvegardes[0].date) : '—'}
                    </div>
                    <div className="sa-kpi-lbl">Dernière sauvegarde</div>
                  </div>
                  <div className="sa-kpi sa-kpi-amber">
                    <div className="sa-kpi-val sa-cle" style={{ fontSize: 12 }}>{dossierSvg || '—'}</div>
                    <div className="sa-kpi-lbl">Dossier sur le serveur</div>
                  </div>
                  <div className="sa-kpi sa-kpi-green">
                    <div className="sa-kpi-val" style={{ fontSize: 15, fontWeight: 600 }}>23h00</div>
                    <div className="sa-kpi-lbl">Sauvegarde automatique</div>
                  </div>
                </div>

                <div className="sa-card">
                  <table className="sa-table">
                    <thead>
                      <tr><th>Fichier</th><th>Taille</th><th>Date &amp; heure</th><th>Type</th></tr>
                    </thead>
                    <tbody>
                      {sauvegardes.map((s) => (
                        <tr key={s.nom}>
                          <td className="sa-cle">{s.nom}</td>
                          <td className="sa-limites">{s.taille}</td>
                          <td className="sa-limites">{fmtDateHeure(s.date)}</td>
                          <td>
                            <span className={`sa-badge ${s.type === 'manuelle' ? 'sa-amber' : 'sa-green'}`}>
                              {s.type === 'manuelle' ? 'Manuelle' : 'Automatique'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {sauvegardes.length === 0 && (
                        <tr><td colSpan={4} className="sa-empty">Aucune sauvegarde pour le moment.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* MODALE ENTREPRISE */}
      {showEntreprise && (
        <div className="sa-modal-overlay" onClick={() => setShowEntreprise(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>{entrepriseEnEdition ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}</h2>
              <button className="sa-modal-close" onClick={() => setShowEntreprise(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              <div className="sa-form-group">
                <label>Nom <span className="sa-req">*</span></label>
                <input value={formEntreprise.nom} onChange={(e) => setFormEntreprise((f) => ({ ...f, nom: e.target.value }))} placeholder="Ex: Boutique du Faso" />
              </div>
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>Email</label>
                  <input type="email" value={formEntreprise.email} onChange={(e) => setFormEntreprise((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="sa-form-group">
                  <label>Téléphone</label>
                  <input value={formEntreprise.telephone} onChange={(e) => setFormEntreprise((f) => ({ ...f, telephone: e.target.value }))} />
                </div>
              </div>
              <div className="sa-form-group">
                <label>Adresse</label>
                <input value={formEntreprise.adresse} onChange={(e) => setFormEntreprise((f) => ({ ...f, adresse: e.target.value }))} />
              </div>
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>IFU</label>
                  <input value={formEntreprise.ifu} onChange={(e) => setFormEntreprise((f) => ({ ...f, ifu: e.target.value }))} />
                </div>
                <div className="sa-form-group">
                  <label>RCCM</label>
                  <input value={formEntreprise.rccm} onChange={(e) => setFormEntreprise((f) => ({ ...f, rccm: e.target.value }))} />
                </div>
              </div>
              {!entrepriseEnEdition && (
                <>
                  <div className="sa-form-divider">Compte administrateur</div>
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label>Prénom</label>
                      <input value={formEntreprise.adminPrenom} onChange={(e) => setFormEntreprise((f) => ({ ...f, adminPrenom: e.target.value }))} />
                    </div>
                    <div className="sa-form-group">
                      <label>Nom</label>
                      <input value={formEntreprise.adminNom} onChange={(e) => setFormEntreprise((f) => ({ ...f, adminNom: e.target.value }))} />
                    </div>
                  </div>
                  <div className="sa-form-row">
                    <div className="sa-form-group">
                      <label>Email de connexion <span className="sa-req">*</span></label>
                      <input type="email" value={formEntreprise.adminEmail} onChange={(e) => setFormEntreprise((f) => ({ ...f, adminEmail: e.target.value }))} placeholder="admin@entreprise.bf" />
                    </div>
                    <div className="sa-form-group">
                      <label>Mot de passe <span className="sa-req">*</span></label>
                      <input type="text" value={formEntreprise.adminPassword} onChange={(e) => setFormEntreprise((f) => ({ ...f, adminPassword: e.target.value }))} placeholder="6 caractères minimum" />
                    </div>
                  </div>

                  <div className="sa-form-divider">Abonnement</div>
                  <div className="sa-form-group">
                    <label>Plan <span className="sa-req">*</span></label>
                    <div className="sa-plan-grid">
                      {PLANS.map((p) => (
                        <div
                          key={p.val}
                          className={`sa-plan-opt ${creationPlan === p.val ? 'active' : ''}`}
                          onClick={() => setCreationPlan(p.val)}
                        >
                          <div className="sa-plan-nom">{p.label}</div>
                          <div className="sa-plan-lim">{p.limites}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="sa-form-group">
                    <label>Durée (mois)</label>
                    <select value={creationDuree} onChange={(e) => setCreationDuree(Number(e.target.value))}>
                      <option value={1}>1 mois</option>
                      <option value={3}>3 mois</option>
                      <option value={6}>6 mois</option>
                      <option value={12}>12 mois</option>
                      <option value={24}>24 mois</option>
                    </select>
                  </div>
                  <p className="sa-hint">Le compte administrateur pourra se connecter immédiatement avec ces identifiants ; la licence active l'entreprise.</p>
                </>
              )}
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowEntreprise(false)}>Annuler</button>
              <button className="sa-btn-primary" onClick={sauvegarderEntreprise}>
                {entrepriseEnEdition ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE LICENCE */}
      {showLicence && (
        <div className="sa-modal-overlay" onClick={() => setShowLicence(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>Émettre une licence</h2>
              <button className="sa-modal-close" onClick={() => setShowLicence(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              <div className="sa-form-group">
                <label>Entreprise <span className="sa-req">*</span></label>
                <select
                  value={formLicence.entrepriseId}
                  onChange={(e) => setFormLicence((f) => ({ ...f, entrepriseId: e.target.value === '' ? '' : Number(e.target.value) }))}
                >
                  <option value="">— Sélectionner —</option>
                  {entreprises.map((e) => (
                    <option key={e.id} value={e.id}>{e.nom}</option>
                  ))}
                </select>
              </div>
              <div className="sa-form-group">
                <label>Plan <span className="sa-req">*</span></label>
                <div className="sa-plan-grid">
                  {PLANS.map((p) => (
                    <div
                      key={p.val}
                      className={`sa-plan-opt ${formLicence.plan === p.val ? 'active' : ''}`}
                      onClick={() => majPlanDuree(p.val, formLicence.dureeMois)}
                    >
                      <div className="sa-plan-nom">{p.label} · {fmtMontant(TARIF_MENSUEL[p.val])}/mois</div>
                      <div className="sa-plan-lim">{p.limites}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>Durée</label>
                  <select
                    value={formLicence.dureeHeures > 0 ? 'test' : String(formLicence.dureeMois)}
                    onChange={(e) => majDuree(e.target.value)}
                  >
                    <option value="test">72 heures (test)</option>
                    <option value="1">1 mois</option>
                    <option value="3">3 mois</option>
                    <option value="6">6 mois</option>
                    <option value="12">12 mois</option>
                    <option value="24">24 mois</option>
                  </select>
                </div>
                <div className="sa-form-group">
                  <label>Montant (FCFA)</label>
                  <input type="number" min={0} value={formLicence.montant}
                    disabled={formLicence.dureeHeures > 0}
                    onChange={(e) => setFormLicence((f) => ({ ...f, montant: Number(e.target.value) }))} />
                </div>
              </div>
              {formLicence.dureeHeures > 0 && (
                <p className="sa-hint">🧪 Licence de test : accès complet pendant 72 h, gratuite. L'entreprise sera automatiquement bloquée à l'échéance.</p>
              )}
              <div className="sa-form-group">
                <label>Paiement</label>
                <select value={formLicence.statutPaiement}
                  onChange={(e) => setFormLicence((f) => ({ ...f, statutPaiement: e.target.value as StatutPaiement }))}>
                  <option value="impaye">Impayé</option>
                  <option value="paye">Payé</option>
                  <option value="partiel">Partiel</option>
                </select>
              </div>
              <div className="sa-form-group">
                <label>Notes</label>
                <input value={formLicence.notes} onChange={(e) => setFormLicence((f) => ({ ...f, notes: e.target.value }))} placeholder="Référence paiement, contact…" />
              </div>
              <p className="sa-hint">Le montant est pré-rempli selon le plan et la durée — ajustez-le si besoin. L'émission révoque la licence active précédente, applique le plan et réactive l'entreprise.</p>
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowLicence(false)}>Annuler</button>
              <button className="sa-btn-primary" onClick={emettreLicence}>Émettre la licence</button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE RENOUVELLEMENT DE LICENCE */}
      {showRenouveler && licenceARenouveler && (
        <div className="sa-modal-overlay" onClick={() => setShowRenouveler(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>Renouveler la licence</h2>
              <button className="sa-modal-close" onClick={() => setShowRenouveler(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              <p className="sa-hint">
                {nomEntreprise(licenceARenouveler.entrepriseId)} · {planLabel(licenceARenouveler.plan)}<br />
                Clé <span className="sa-cle">{licenceARenouveler.cle}</span><br />
                Expiration actuelle : {fmtDate(licenceARenouveler.dateExpiration)}
              </p>
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>Prolonger de</label>
                  <select
                    value={renouvellerHeures > 0 ? 'test' : String(renouvellerDuree)}
                    onChange={(e) => majRenouvellerDuree(e.target.value)}
                  >
                    <option value="test">72 heures (test)</option>
                    <option value="1">1 mois</option>
                    <option value="3">3 mois</option>
                    <option value="6">6 mois</option>
                    <option value="12">12 mois</option>
                    <option value="24">24 mois</option>
                  </select>
                </div>
                <div className="sa-form-group">
                  <label>Montant (FCFA)</label>
                  <input type="number" min={0} value={renouvellerMontant}
                    disabled={renouvellerHeures > 0}
                    onChange={(e) => setRenouvellerMontant(Number(e.target.value))} />
                </div>
              </div>
              <div className="sa-form-group">
                <label>Paiement</label>
                <select value={renouvellerPaiement}
                  onChange={(e) => setRenouvellerPaiement(e.target.value as StatutPaiement)}>
                  <option value="paye">Payé</option>
                  <option value="impaye">Impayé</option>
                  <option value="partiel">Partiel</option>
                </select>
              </div>
              {renouvellerHeures > 0 && (
                <p className="sa-hint">🧪 Prolongation de test : +72 h d'accès complet, gratuite.</p>
              )}
              <p className="sa-hint">
                La même clé est conservée. Si la licence est encore valable, les jours
                restants sont ajoutés (aucun jour perdu). L'entreprise est réactivée.
              </p>
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowRenouveler(false)}>Annuler</button>
              <button className="sa-btn-primary" onClick={confirmerRenouvellement}>Renouveler</button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE DÉTAIL ENTREPRISE */}
      {showDetail && detailEntreprise && (
        <div className="sa-modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="sa-modal sa-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>{detailEntreprise.nom}</h2>
              <button className="sa-modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              {detailChargement ? (
                <div className="sa-loading">Chargement…</div>
              ) : (
                <>
                  {/* Usage vs limites */}
                  <div className="sa-form-divider">Utilisation (plan {planLabel(detailEntreprise.plan)})</div>
                  <div className="sa-kpi-grid">
                    {detailUsage && ([
                      { lbl: 'Utilisateurs', u: detailUsage.utilisateurs },
                      { lbl: 'Boutiques', u: detailUsage.boutiques },
                      { lbl: 'Produits', u: detailUsage.produits },
                    ]).map((x) => (
                      <div key={x.lbl} className="sa-kpi sa-kpi-blue">
                        <div className="sa-kpi-val">{x.u.count}<span style={{ fontSize: 14, color: '#94a3b8' }}> / {x.u.max}</span></div>
                        <div className="sa-kpi-lbl">{x.lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Utilisateurs */}
                  <div className="sa-form-divider">
                    Utilisateurs
                    <button className="sa-btn-mini" style={{ float: 'right' }} onClick={() => setShowAdminForm((v) => !v)}>
                      {showAdminForm ? 'Annuler' : '+ Ajouter un administrateur'}
                    </button>
                  </div>

                  {showAdminForm && (
                    <div className="sa-card" style={{ padding: 14, marginBottom: 14 }}>
                      <div className="sa-form-row">
                        <div className="sa-form-group"><label>Prénom</label>
                          <input value={formAdmin.prenom} onChange={(e) => setFormAdmin((f) => ({ ...f, prenom: e.target.value }))} /></div>
                        <div className="sa-form-group"><label>Nom</label>
                          <input value={formAdmin.nom} onChange={(e) => setFormAdmin((f) => ({ ...f, nom: e.target.value }))} /></div>
                      </div>
                      <div className="sa-form-row">
                        <div className="sa-form-group"><label>Email <span className="sa-req">*</span></label>
                          <input type="email" value={formAdmin.email} onChange={(e) => setFormAdmin((f) => ({ ...f, email: e.target.value }))} /></div>
                        <div className="sa-form-group"><label>Mot de passe <span className="sa-req">*</span></label>
                          <input type="text" value={formAdmin.password} onChange={(e) => setFormAdmin((f) => ({ ...f, password: e.target.value }))} placeholder="6 caractères min." /></div>
                      </div>
                      <button className="sa-btn-primary" onClick={ajouterAdmin}>Créer l'administrateur</button>
                    </div>
                  )}

                  <table className="sa-table">
                    <thead>
                      <tr><th>Utilisateur</th><th>Rôle</th><th>Boutique</th><th>Statut</th><th>Dernière connexion</th><th></th></tr>
                    </thead>
                    <tbody>
                      {detailUsers.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <div className="sa-ent-nom">{u.prenom} {u.nom}</div>
                            <div className="sa-ent-sub">{u.email}</div>
                          </td>
                          <td>{u.role}</td>
                          <td>{u.boutiqueNom || '—'}</td>
                          <td>{u.actif ? <span className="sa-badge sa-green">● Actif</span> : <span className="sa-badge sa-grey">● Inactif</span>}</td>
                          <td>{u.derniereConnexion ? fmtDate(u.derniereConnexion) : 'Jamais'}</td>
                          <td className="sa-actions">
                            <button className="sa-btn-mini" onClick={() => reinitMdp(u)}>Réinit. MdP</button>
                          </td>
                        </tr>
                      ))}
                      {detailUsers.length === 0 && (
                        <tr><td colSpan={6} className="sa-empty">Aucun utilisateur. Ajoutez un administrateur pour activer l'accès.</td></tr>
                      )}
                    </tbody>
                  </table>

                  {/* Historique des licences */}
                  <div className="sa-form-divider">Historique des licences</div>
                  <table className="sa-table">
                    <thead>
                      <tr><th>Clé</th><th>Plan</th><th>Statut</th><th>Début</th><th>Expiration</th></tr>
                    </thead>
                    <tbody>
                      {detailLicences.map((l) => (
                        <tr key={l.id}>
                          <td className="sa-cle">{l.cle}</td>
                          <td>{planLabel(l.plan)}</td>
                          <td>{badgeLicence(l.statut)}</td>
                          <td>{fmtDate(l.dateDebut)}</td>
                          <td>{fmtDate(l.dateExpiration)}</td>
                        </tr>
                      ))}
                      {detailLicences.length === 0 && (
                        <tr><td colSpan={5} className="sa-empty">Aucune licence émise.</td></tr>
                      )}
                    </tbody>
                  </table>

                  {/* Zone danger */}
                  <div className="sa-form-divider sa-divider-danger">Zone dangereuse</div>
                  <div className="sa-danger-zone">
                    <div>
                      <strong>Supprimer définitivement cette entreprise</strong>
                      <p>Efface TOUTES ses données : utilisateurs, boutiques, produits, ventes, clients, licences… Action irréversible.</p>
                    </div>
                    <button
                      className="sa-btn-danger"
                      onClick={() => { setConfirmationNom(''); setShowSuppression(true); }}>
                      Supprimer…
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowDetail(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE SUPPRESSION DEFINITIVE */}
      {showSuppression && detailEntreprise && (
        <div className="sa-modal-overlay" onClick={() => !suppressionEnCours && setShowSuppression(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>⚠️ Supprimer « {detailEntreprise.nom} »</h2>
              <button className="sa-modal-close" onClick={() => setShowSuppression(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              <div className="sa-alert sa-alert-err">
                Cette action est <strong>IRRÉVERSIBLE</strong>. Toutes les données de
                l'entreprise seront définitivement effacées : utilisateurs, boutiques,
                produits, stocks, clients, fournisseurs, ventes, commandes, dépenses,
                licences et configurations.
              </div>
              <div className="sa-form-group">
                <label>
                  Pour confirmer, tapez le nom exact de l'entreprise :{' '}
                  <strong>{detailEntreprise.nom}</strong>
                </label>
                <input
                  value={confirmationNom}
                  onChange={(e) => setConfirmationNom(e.target.value)}
                  placeholder={detailEntreprise.nom}
                  autoFocus
                />
              </div>
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowSuppression(false)} disabled={suppressionEnCours}>
                Annuler
              </button>
              <button
                className="sa-btn-danger"
                onClick={supprimerEntreprise}
                disabled={confirmationNom !== detailEntreprise.nom || suppressionEnCours}>
                {suppressionEnCours ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE CHANGEMENT DE MOT DE PASSE */}
      {showMdp && (
        <div className="sa-modal-overlay" onClick={() => setShowMdp(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-head">
              <h2>Changer mon mot de passe</h2>
              <button className="sa-modal-close" onClick={() => setShowMdp(false)}>×</button>
            </div>
            <div className="sa-modal-body">
              {erreur && <div className="sa-alert sa-alert-err">⚠️ {erreur}</div>}
              <div className="sa-form-group">
                <label>Mot de passe actuel <span className="sa-req">*</span></label>
                <input type="password" value={formMdp.ancien} onChange={(e) => setFormMdp((f) => ({ ...f, ancien: e.target.value }))} />
              </div>
              <div className="sa-form-group">
                <label>Nouveau mot de passe <span className="sa-req">*</span></label>
                <input type="password" value={formMdp.nouveau} onChange={(e) => setFormMdp((f) => ({ ...f, nouveau: e.target.value }))} placeholder="6 caractères min." />
              </div>
              <div className="sa-form-group">
                <label>Confirmer <span className="sa-req">*</span></label>
                <input type="password" value={formMdp.confirme} onChange={(e) => setFormMdp((f) => ({ ...f, confirme: e.target.value }))} />
              </div>
            </div>
            <div className="sa-modal-foot">
              <button className="sa-btn-secondary" onClick={() => setShowMdp(false)}>Annuler</button>
              <button className="sa-btn-primary" onClick={changerMonMdp}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
